import React, { useMemo, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { exportToCSV } from "react-easy-export/dist/react-easy-export";
import { ToastTypes, useMessage } from "../../../../base/context/MessageProvider";
import { useLoader } from "../../../../base/context/LoaderProvider";
import { orderApiService } from "../../../../service/admin/ordersApiService";
import { FiEye, FiEyeOff } from "react-icons/fi";
import ExcelTable from "../../../library/excelTable";
import OrderPlatformBadge from "../../../library/orderPlatformBadge/OrderPlatformBadge";
import { getOrderPlatformMeta } from "../../../../utils/orderMarketplacePlatform";
import "./FailedOrderTable.css";

function normRecipientPart(x) {
  return x && String(x).trim() && String(x).trim() !== "-" ? String(x).trim() : "";
}

function failedOrderRecipientFilterAccessor(_, row) {
  const nameRaw = normRecipientPart(row?.recipientName);
  const phone = normRecipientPart(row?.shippingPhoneNumber);
  const street = [row?.shippingAddress1, row?.shippingAddress2, row?.shippingAddress3]
    .map(normRecipientPart)
    .filter(Boolean)
    .join(", ");
  const loc = [row?.shippingCity, row?.shippingState, row?.shippingPostalCode]
    .map(normRecipientPart)
    .filter(Boolean)
    .join(", ");
  return [nameRaw, phone, street, loc].filter(Boolean).join(" | ") || null;
}

function FailedOrdersRecipientCell({ row }) {
  const nameRaw = normRecipientPart(row?.recipientName);
  const phone = normRecipientPart(row?.shippingPhoneNumber);
  const shippingAddress = [
    row?.shippingAddress1,
    row?.shippingAddress2,
    row?.shippingAddress3,
    row?.shippingCity,
    row?.shippingState,
    row?.shippingPostalCode,
  ]
    .map(normRecipientPart)
    .filter(Boolean)
    .join(", ");

  const billingAddress = [
    row?.billingAddress1,
    row?.billingAddress2,
    row?.billingAddress3,
    row?.billingCity,
    row?.billingState,
    row?.billingPostalCode,
  ]
    .map(normRecipientPart)
    .filter(Boolean)
    .join(", ");

  const displayLabel = nameRaw || phone || "-";
  const hasDetails = Boolean(phone || shippingAddress || billingAddress);

  const handleRecipientTooltipEnter = (e) => {
    const tooltip = e.currentTarget.querySelector(".address-tooltip-card");
    if (!tooltip) return;
    const rect = e.currentTarget.getBoundingClientRect();
    tooltip.style.top = `${rect.bottom + 5}px`;
    tooltip.style.left = `${rect.left}px`;
  };

  if (!hasDetails) {
    return (
      <span className="failed-order-recipient-plain">{displayLabel}</span>
    );
  }

  return (
    <div
      className="recipient-tooltip-wrapper"
      onMouseEnter={handleRecipientTooltipEnter}
    >
      <span className="recipient-name">{displayLabel}</span>
      <div className="address-tooltip-card">
        {phone ? (
          <div className="address-section">
            <strong>Mobile:</strong>
             <span>{phone}</span>
          </div>
        ) : null}
        {shippingAddress ? (
          <div className="address-section">
            <strong>Shipping Address:</strong>
            <span>{shippingAddress}</span>
          </div>
        ) : null}
        {billingAddress ? (
          <div className="address-section">
            <strong>Billing Address:</strong>
            <span>{billingAddress}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const FailedOrderTable = ({ data, pages, limit, page, showOwnAccountBadge = false, showFullExcelData = false, onDataChange, isLoading = false, loadingText = "Loading..." }) => {
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [remarkModal, setRemarkModal] = useState({ open: false, order: null, remark: "" });
  const [deleteModal, setDeleteModal] = useState({ open: false, order: null, password: "", showPassword: false });
  const [bulkDeleteModal, setBulkDeleteModal] = useState({ open: false, password: "", showPassword: false });
  const navigate = useNavigate();
  const message = useMessage();
  const loader = useLoader();

  const currentPage = Number(pages?.currentPage) || 1;
  const pageSize = Number(limit) || 0;
  const effectivePageSize = pageSize || (data?.length ?? 0);

  // Flatten data for full Excel view (one row per orderDetail)
  const flattenedData = useMemo(() => {
    if (!showFullExcelData) return null;
    
    let serialNum = (currentPage - 1) * effectivePageSize;
    return (data || []).flatMap((order) =>
      (order.orderDetails || []).map((detail, idx) => {
        serialNum++;
        // Get India S.No. from originalExcelData
        const indiaSNo = order.originalExcelData?.['India S.No'] || order.originalExcelData?.['India S.No.'] || '-';
        return {
          _id: `${order._id}-${idx}`,
          originalOrderId: order._id, // Store original order _id for API calls
          serialNumber: serialNum,
          indiaSNo: indiaSNo,
          orderId: order.orderId,
          orderItemId: detail.orderItemId || '-',
          sku: detail.sku || '-',
          ASIN: detail.ASIN || '-',
          productName: detail.productName || '-',
          productUrl: detail.productUrl || '-',
          quantity: detail.quantity || 1,
          consumerPrice: detail.consumerPrice || 0,
          orderType: order.orderType || '-',
          panel: order.panel || '-',
          // Use original date strings if available
          orderDateStr: order.orderDateStr || (order.orderDate ? moment(order.orderDate).format('DD-MMM') : '-'),
          shipByDateStr: order.shipByDateStr || (order.shipByDate ? moment(order.shipByDate).format('DD-MMM') : '-'),
          deliverByDateStr: order.deliverByDateStr || (order.deliverByDate ? moment(order.deliverByDate).format('DD-MMM') : '-'),
          recipientName: order.recipientName || '-',
          shippingAddress1: order.shippingAddress1 || '-',
          shippingAddress2: order.shippingAddress2 || '',
          shippingAddress3: order.shippingAddress3 || '',
          shippingCity: order.shippingCity || '-',
          shippingState: order.shippingState || '-',
          shippingPostalCode: order.shippingPostalCode || '-',
          shippingPhoneNumber: order.shippingPhoneNumber || '-',
          sellerName: `${order.sellerId?.firstName || ''} ${order.sellerId?.lastName || ''}`.trim() || '-',
          sellerEmail: order.sellerId?.email || '-',
          sellerUniqueId: order.sellerId?.uniqueId || '-',
          remark: order.remark || '', // Add remark field
          platformMeta: getOrderPlatformMeta(order),
          createdAt: order.createdAt,
          status: order.status || 'abnormal',
          orderDate: order.orderDate,
          shipByDate: order.shipByDate,
          deliverByDate: order.deliverByDate,
          // Store full originalExcelData for reference
          originalExcelData: order.originalExcelData || {},
        };
      })
    );
  }, [data, showFullExcelData, currentPage, effectivePageSize]);

  const tableData = useMemo(
    () =>
      (data || []).map((item, index) => ({
        ...item,
        serialNumber: (currentPage - 1) * effectivePageSize + index + 1,
        indiaSNo: item?.originalExcelData?.['India S.No'] || item?.originalExcelData?.['India S.No.'] || '-',
      })),
    [data, currentPage, effectivePageSize]
  );

  const getSellerName = (order) => {
    const seller = order?.sellerId;
    if (!seller) return "-";
    return `${seller.firstName || ""} ${seller.lastName || ""}`.trim() || "-";
  };

  const getSellerEmail = (order) => {
    return order?.sellerId?.email || "-";
  };

  const getOrderDetails = (order, field) => {
    if (!order?.orderDetails || order.orderDetails.length === 0) return "-";
    return order.orderDetails.map((item) => item?.[field] || "-").join(", ");
  };

  const getTotalQuantity = (order) => {
    if (!order?.orderDetails || order.orderDetails.length === 0) return 0;
    return order.orderDetails.reduce((sum, item) => sum + (item?.quantity || 0), 0);
  };

  const getTotalPrice = (order) => {
    if (!order?.orderDetails || order.orderDetails.length === 0) return 0;
    return order.orderDetails.reduce(
      (sum, item) => sum + (parseFloat(item?.consumerPrice) || 0) * (item?.quantity || 1),
      0
    );
  };

  const handleSelectionChange = (selected) => {
    setSelectedOrders(selected);
  };

  const handleExportCsv = () => {
    const exportData = selectedOrders.length > 0 ? selectedOrders : tableData;

    if (!exportData.length) {
      message.showToast("No orders to export", ToastTypes.WARNING);
      return;
    }

    const csvRows = exportData.map((item, index) => ({
      "India S.No.": item?.originalExcelData?.['India S.No'] || item?.originalExcelData?.['India S.No.'] || item?.indiaSNo || "",
      "S.No.": item?.serialNumber ?? index + 1,
      "Order ID": item?.orderId || "",
      "Seller Name": getSellerName(item),
      "User ID": item?.sellerId?.uniqueId || "",
      "Seller Email": getSellerEmail(item),
      "Own Account": item?.sellerId?.ownAccount ? "Yes" : "No",
      "SKUs": getOrderDetails(item, "sku"),
      "Product Names": getOrderDetails(item, "productName"),
      "Total Quantity": getTotalQuantity(item),
      "Total Price": getTotalPrice(item).toFixed(2),
      "Recipient Name": item?.recipientName || "",
      "Shipping City": item?.shippingCity || "",
      "Shipping State": item?.shippingState || "",
      "Shipping Postal Code": item?.shippingPostalCode || "",
      "Status": item?.status || "abnormal",
      "Remark": item?.remark || "",
      "Order Date": item?.orderDate
        ? moment(item.orderDate).format("DD/MM/YYYY")
        : "",
      "Created At": item?.createdAt
        ? moment(item.createdAt).format("DD/MM/YYYY HH:mm")
        : "",
    }));

    exportToCSV(csvRows, `failed_orders_${Date.now()}`);
  };

  const handleRowNavigate = (order) => {
    if (!order?.sellerId?._id) return;
    navigate(`/admin/user-management/edit-user/${order.sellerId._id}`);
  };

  const handleRemarkClick = (order) => {
    setRemarkModal({
      open: true,
      order: order,
      remark: order?.remark || "",
    });
  };

  const handleRemarkSave = async () => {
    if (!remarkModal.order?._id) return;

    loader.showInlineLoader("Updating remark...", "medium");
    try {
      const res = await orderApiService.updateFailedOrderRemark(remarkModal.order._id, remarkModal.remark);
      loader.hideLoader();
      
      if (res.status.code === 200) {
        message.showToast("Remark updated successfully", ToastTypes.SUCCESS);
        setRemarkModal({ open: false, order: null, remark: "" });
        if (onDataChange) {
          onDataChange();
        }
      } else {
        message.showToast(res.error?.message || "Failed to update remark", ToastTypes.ERROR);
      }
    } catch (err) {
      loader.hideLoader();
      message.showToast(err?.message || "Error updating remark", ToastTypes.ERROR);
    }
  };

  const handleDeleteClick = (order) => {
    setDeleteModal({
      open: true,
      order: order,
      password: "",
      showPassword: false,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.order?._id || !deleteModal.password) {
      message.showToast("Please enter password", ToastTypes.WARNING);
      return;
    }

    loader.showInlineLoader("Deleting order...", "medium");
    try {
      const res = await orderApiService.deleteFailedOrder(deleteModal.order._id, deleteModal.password);
      loader.hideLoader();
      
      if (res.status.code === 200) {
        message.showToast("Failed order deleted successfully", ToastTypes.SUCCESS);
        setDeleteModal({ open: false, order: null, password: "", showPassword: false });
        if (onDataChange) {
          onDataChange();
        }
      } else {
        message.showToast(res.error?.message || "Failed to delete order", ToastTypes.ERROR);
      }
    } catch (err) {
      loader.hideLoader();
      message.showToast(err?.message || "Error deleting order", ToastTypes.ERROR);
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedOrders.length === 0) {
      message.showToast("Please select at least one order to delete", ToastTypes.WARNING);
      return;
    }
    setBulkDeleteModal({ open: true, password: "", showPassword: false });
  };

  const handleBulkDeleteConfirm = async () => {
    if (!bulkDeleteModal.password) {
      message.showToast("Please enter password", ToastTypes.WARNING);
      return;
    }

    if (selectedOrders.length === 0) {
      message.showToast("No orders selected", ToastTypes.WARNING);
      return;
    }

    // Get order IDs - handle both regular orders and flattened data
    const orderIds = selectedOrders.map(order => {
      // For flattened data, use originalOrderId if available
      return order.originalOrderId || order._id;
    }).filter(Boolean);

    if (orderIds.length === 0) {
      message.showToast("No valid orders to delete", ToastTypes.WARNING);
      return;
    }

    loader.showInlineLoader(`Deleting ${orderIds.length} order(s)...`, "medium");
    
    try {
      // Use the same deleteFailedOrder API with orderIds for bulk delete
      const res = await orderApiService.deleteFailedOrder(null, bulkDeleteModal.password, orderIds);
      loader.hideLoader();
      
      if (res.status.code === 200) {
        const deletedCount = res.data?.deletedCount || 0;
        const requestedCount = res.data?.requestedCount || orderIds.length;
        
        if (deletedCount === requestedCount) {
          message.showToast(`Successfully deleted ${deletedCount} order(s)`, ToastTypes.SUCCESS);
        } else {
          message.showToast(`Deleted ${deletedCount} of ${requestedCount} order(s)`, ToastTypes.WARNING);
        }
        
        setBulkDeleteModal({ open: false, password: "", showPassword: false });
        setSelectedOrders([]);
        
        if (onDataChange) {
          onDataChange();
        }
      } else {
        message.showToast(res.error?.message || "Failed to delete orders", ToastTypes.ERROR);
      }
    } catch (err) {
      loader.hideLoader();
      message.showToast(err?.message || "Error deleting orders", ToastTypes.ERROR);
    }
  };

  function getStatusBadge(status) {
    const statusConfig = {
      abnormal: { class: "status-abnormal", icon: "fas fa-exclamation-triangle" },
      "payment pending": { class: "status-pending", icon: "fas fa-clock" },
      processing: { class: "status-processing", icon: "fas fa-cog" },
      shipped: { class: "status-shipped", icon: "fas fa-truck" },
      delivered: { class: "status-delivered", icon: "fas fa-check-circle" },
      cancel: { class: "status-cancelled", icon: "fas fa-times-circle" },
      ticket: { class: "status-ticket", icon: "fas fa-ticket-alt" },
    };

    const key = String(status || "abnormal").toLowerCase();
    const config = statusConfig[key] || {
      class: "status-abnormal",
      icon: "fas fa-exclamation-triangle",
    };

    return (
      <span className={`status-badge ${config.class}`}>
        <i className={config.icon}></i>
        {status || "abnormal"}
      </span>
    );
  }

  const columns = [
    {
      key: "serialNumber",
      label: "S.No.",
      width: 70,
      render: (value) => <span className="serial-number-cell">{value ?? "-"}</span>,
    },
    {
      key: "indiaSNo",
      label: "India S.No.",
      width: 90,
      render: (_, row) => {
        const indiaSNo = row?.originalExcelData?.['India S.No'] || row?.originalExcelData?.['India S.No.'] || '-';
        return (
          <span style={{ 
            fontWeight: 600, 
            color: indiaSNo !== '-' ? '#7c3aed' : '#9ca3af',
            backgroundColor: indiaSNo !== '-' ? '#f3e8ff' : 'transparent',
            padding: indiaSNo !== '-' ? '2px 6px' : '0',
            borderRadius: '4px',
            fontSize: '12px'
          }}>
            {indiaSNo}
          </span>
        );
      },
    },
    {
      key: "orderId",
      label: "Order ID",
      width: 160,
      render: (value) => <span className="order-id-cell">{value || "-"}</span>,
    },
    {
      key: "platform",
      label: "Platform",
      width: 120,
      filterAccessor: (_, row) => getOrderPlatformMeta(row).label,
      render: (_, row) => <OrderPlatformBadge order={row} size={18} compactImporterrWordmark />,
    },

    {
      key: "sellerId",
      label: "Seller",
      width: 200,
      filterAccessor: (_, row) => {
        const seller = row?.sellerId;
        if (!seller) return null;
        const name = `${seller.firstName || ""} ${seller.lastName || ""}`.trim();
        const uid = seller.uniqueId || "";
        return [name, uid].filter(Boolean).join(" ") || null;
      },
      render: (_, row) => {
        const seller = row?.sellerId;
        if (!seller) return "-";

        const sellerName = getSellerName(row);
        const uniqueId = seller?.uniqueId;
        const isOwnAccount = seller?.ownAccount === true;
        const isResellerAccount = seller?.resellerAccount === true;

        return (
          <div className="seller-cell">
            <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
              <span
                className="seller-name seller-link"
                style={{ cursor: "pointer", color: "#1677ff" }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin/user-management/edit-user/${seller._id}`);
                }}
              >
                {sellerName}
                {uniqueId ? (
                  <span style={{ fontSize: "10px", color: "#6b7280", fontWeight: 500 }}> ({uniqueId})</span>
                ) : null}
              </span>
              {showOwnAccountBadge && isOwnAccount && (
                <span
                  style={{
                    background: "#fef3c7",
                    color: "#d97706",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    fontSize: "10px",
                    fontWeight: 600,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "3px",
                  }}
                >
                  ⭐ BTPL
                </span>
              )}
              {showOwnAccountBadge && isResellerAccount && (
                <span
                  style={{
                    background: "#dbeafe",
                    color: "#1e40af",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    fontSize: "10px",
                    fontWeight: 600,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "3px",
                  }}
                >
                  🏪 Reseller
                </span>
              )}
            </div>
            {seller?.email && <span className="seller-email">{seller.email}</span>}
          </div>
        );
      },
    },
    {
      key: "orderDetails",
      label: "SKUs",
      width: 180,
      filterAccessor: (details) => {
        const skus = (details || []).map(d => d?.sku).filter(Boolean);
        return skus.length > 0 ? skus.join(", ") : null;
      },
      render: (_, row) => {
        const skus = row?.orderDetails?.map((item) => item?.sku).filter(Boolean) || [];
        if (skus.length === 0) return "-";
        
        return (
          <div className="sku-cell">
            {skus.slice(0, 2).map((sku, idx) => (
              <span key={idx} className="sku-tag">{sku}</span>
            ))}
            {skus.length > 2 && (
              <span className="sku-more">+{skus.length - 2} more</span>
            )}
          </div>
        );
      },
    },
    {
      key: "asins",
      label: "ASINs",
      width: 180,
      filterAccessor: (_, row) => {
        const asins = (row?.orderDetails || []).map(d => d?.ASIN).filter(Boolean);
        return asins.length > 0 ? asins.join(", ") : null;
      },
      render: (_, row) => {
        const asins = row?.orderDetails?.map((item) => item?.ASIN).filter(Boolean) || [];
        if (asins.length === 0) return "-";
        
        return (
          <div className="asin-cell">
            {asins.map((asin, idx) => (
              <span
                key={idx}
                className="asin-link"
                onClick={(e) => {
                  e.stopPropagation();
                  // Open Amazon Product Detail page in new tab with fromFailedOrders parameter
                  window.open(`/admin/amazon-product?asin=${asin}&fromFailedOrders=true`, "_blank");
                }}
                title={`View ${asin} product details`}
              >
                {asin}
                <i className="fas fa-search" style={{ marginLeft: '4px', fontSize: '0.6rem' }}></i>
              </span>
            ))}
          </div>
        );
      },
    },
    {
      key: "productNames",
      label: "Products",
      width: 220,
      filterAccessor: (_, row) => {
        const products = (row?.orderDetails || []).map(d => d?.productName).filter(Boolean);
        return products.length > 0 ? products.join(", ") : null;
      },
      render: (_, row) => {
        const products = row?.orderDetails?.map((item) => item?.productName).filter(Boolean) || [];
        if (products.length === 0) return "-";
        
        const displayText = products[0]?.substring(0, 40) + (products[0]?.length > 40 ? "..." : "");
        
        return (
          <div className="product-cell" title={products.join(", ")}>
            <span>{displayText}</span>
            {products.length > 1 && (
              <span className="product-more">+{products.length - 1} more</span>
            )}
          </div>
        );
      },
    },
    {
      key: "quantity",
      label: "Qty",
      width: 80,
      filterAccessor: (_, row) => getTotalQuantity(row),
      render: (_, row) => <span className="qty-cell">{getTotalQuantity(row)}</span>,
    },
    {
      key: "totalPrice",
      label: "Total Price",
      width: 120,
      filterAccessor: (_, row) => getTotalPrice(row).toFixed(2),
      render: (_, row) => (
        <span className="price-cell">₹{getTotalPrice(row).toFixed(2)}</span>
      ),
    },
    {
      key: "recipientName",
      label: "Recipient",
      width: 120,
      filterAccessor: failedOrderRecipientFilterAccessor,
      render: (_, row) => <FailedOrdersRecipientCell row={row} />,
    },
    {
      key: "status",
      label: "Status",
      width: 130,
      render: (value) => getStatusBadge(value),
    },
    {
      key: "remark",
      label: "Remark",
      width: 140,
      render: (_, row) => {
        const remark = row?.remark || "";
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}>
            <span
              style={{
                flex: 1,
                maxWidth: "140px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: "13px",
                color: remark ? "#374151" : "#9ca3af",
                fontWeight: remark ? 400 : 300,
              }}
              title={remark || "No remark - Click to add"}
            >
              {remark || "-"}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRemarkClick(row);
              }}
              style={{
                padding: "4px 10px",
                fontSize: "12px",
                border: "1px solid #3b82f6",
                background: remark ? "#eff6ff" : "#fff",
                borderRadius: "4px",
                cursor: "pointer",
                color: "#3b82f6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#3b82f6";
                e.currentTarget.style.color = "#fff";
                e.currentTarget.style.borderColor = "#3b82f6";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = remark ? "#eff6ff" : "#fff";
                e.currentTarget.style.color = "#3b82f6";
                e.currentTarget.style.borderColor = "#3b82f6";
              }}
              title={remark ? "Edit remark" : "Add remark"}
            >
              <i className="fas fa-edit" style={{ fontSize: "11px" }}>{remark ? "Edit" : "Add"}</i>
            </button>
          </div>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      width: 80,
      render: (_, row) => (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(row);
            }}
            style={{
              padding: "4px 6px",
              fontSize: "14px",
              border: "1px solid #dc2626",
              background: "#fff",
              borderRadius: "4px",
              cursor: "pointer",
              color: "#dc2626",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
              boxShadow: "0 1px 3px rgba(220, 38, 38, 0.2)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#dc2626";
              e.currentTarget.style.color = "#fff";
              e.currentTarget.style.borderColor = "#dc2626";
              e.currentTarget.style.transform = "scale(1.1)";
              e.currentTarget.style.boxShadow = "0 2px 6px rgba(220, 38, 38, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#fff";
              e.currentTarget.style.color = "#dc2626";
              e.currentTarget.style.borderColor = "#dc2626";
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow = "0 1px 3px rgba(220, 38, 38, 0.2)";
            }}
            title="Delete order (requires password)"
          >
            <i className="fas fa-trash" style={{ fontSize: "12px" }}>Delete</i>
          </button>
        </div>
      ),
    },
    {
      key: "createdAt",
      label: "Created At",
      width: 150,
      render: (value) =>
        value ? (
          <div className="date-cell">
            <span>{moment(value).format("DD MMM YYYY")}</span>
            <span className="date-time">{moment(value).format("HH:mm")}</span>
          </div>
        ) : (
          "-"
        ),
    },
  ];

  // Full Excel data columns for Own Accounts tab
  const fullExcelColumns = [
    {
      key: "serialNumber",
      label: "SNO",
      width: 60,
      render: (value) => <span className="serial-number-cell">{value ?? "-"}</span>,
    },
    {
      key: "indiaSNo",
      label: "India S.No.",
      width: 90,
      render: (value) => (
        <span style={{ 
          fontWeight: 600, 
          color: value && value !== '-' ? '#7c3aed' : '#9ca3af',
          backgroundColor: value && value !== '-' ? '#f3e8ff' : 'transparent',
          padding: value && value !== '-' ? '2px 6px' : '0',
          borderRadius: '4px'
        }}>
          {value || '-'}
        </span>
      ),
    },
    {
      key: "orderId",
      label: "Order ID",
      width: 180,
      render: (value) => (
        <span style={{ color: "#3b82f6", fontWeight: 500 }}>{value || "-"}</span>
      ),
    },
    {
      key: "platformMeta",
      label: "Platform",
      width: 120,
      render: (_, row) => (
        <OrderPlatformBadge meta={row.platformMeta} size={16} compactImporterrWordmark />
      ),
    },
    {
      key: "orderItemId",
      label: "Order Item ID",
      width: 150,
      render: (value) => <span style={{ fontFamily: "monospace", fontSize: "12px" }}>{value || "-"}</span>,
    },
    {
      key: "orderDateStr",
      label: "Order Date",
      width: 100,
      render: (value, row) => {
        if (value && value !== '-') return value;
        if (row?.orderDate) return moment(row.orderDate).format('DD-MMM');
        return '-';
      },
    },
    {
      key: "orderType",
      label: "Order Type",
      width: 100,
      render: (value) => <span>{value || "-"}</span>,
    },
    {
      key: "panel",
      label: "Panel",
      width: 100,
      render: (value) => <span>{value || "-"}</span>,
    },
    {
      key: "sellerName",
      label: "Seller",
      width: 170,
      filterAccessor: (_, row) => {
        const name = row?.sellerName && row.sellerName !== "-" ? row.sellerName : "";
        const uid = row?.sellerUniqueId && row.sellerUniqueId !== "-" ? row.sellerUniqueId : "";
        return [name, uid].filter(Boolean).join(" ") || null;
      },
      render: (value, row) => {
        const sellerEmail = row?.sellerEmail || "-";
        const uid = row?.sellerUniqueId && row.sellerUniqueId !== "-" ? row.sellerUniqueId : null;
        return (
          <div>
            <span style={{ color: "#1677ff", cursor: "pointer", fontWeight: 500 }}>
              {value || "-"}
              {uid ? (
                <span style={{ fontSize: "10px", color: "#6b7280", fontWeight: 500 }}> ({uid})</span>
              ) : null}
            </span>
            {sellerEmail !== "-" && (
              <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "2px" }}>
                {sellerEmail}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "productName",
      label: "Product Name",
      width: 250,
      render: (value) => (
        <span
          style={{
            maxWidth: "250px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "block",
          }}
          title={value}
        >
          {value || "-"}
        </span>
      ),
    },
    {
      key: "sku",
      label: "SKU",
      width: 180,
      render: (value) => (
        <span style={{ fontFamily: "monospace", fontSize: "12px" }}>{value || "-"}</span>
      ),
    },
    {
      key: "ASIN",
      label: "ASIN",
      width: 120,
      render: (value) =>
        value && value !== "-" ? (
          <span
            style={{
              background: "#fef3c7",
              color: "#92400e",
              padding: "2px 8px",
              borderRadius: "4px",
              fontSize: "12px",
              fontWeight: 500,
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              window.open(`/admin/amazon-product?asin=${value}&fromFailedOrders=true`, "_blank");
            }}
          >
            {value}
          </span>
        ) : (
          "-"
        ),
    },
    {
      key: "productUrl",
      label: "URL",
      width: 100,
      render: (value) =>
        value && value !== "-" ? (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#3b82f6", textDecoration: "underline", fontSize: "12px" }}
          >
            Link
          </a>
        ) : (
          "-"
        ),
    },
    {
      key: "quantity",
      label: "Qty",
      width: 60,
      render: (value) => <span>{value || 0}</span>,
    },
    {
      key: "consumerPrice",
      label: "Order Value",
      width: 130,
      render: (value) => <span style={{ fontWeight: 500 }}>₹{Number(value || 0).toFixed(2)}</span>,
    },
    {
      key: "shipByDateStr",
      label: "Ship By",
      width: 110,
      render: (value, row) => {
        if (value && value !== '-') return value;
        if (row?.shipByDate) return moment(row.shipByDate).format('DD-MMM');
        return '-';
      },
    },
    {
      key: "deliverByDateStr",
      label: "Deliver By",
      width: 100,
      render: (value, row) => {
        if (value && value !== '-') return value;
        if (row?.deliverByDate) return moment(row.deliverByDate).format('DD-MMM');
        return '-';
      },
    },
    {
      key: "recipientName",
      label: "Recipient",
      width: 120,
      filterAccessor: failedOrderRecipientFilterAccessor,
      render: (_, row) => <FailedOrdersRecipientCell row={row} />,
    },
    {
      key: "status",
      label: "Status",
      width: 130,
      render: (value) => getStatusBadge(value),
    },
    {
      key: "createdAt",
      label: "Created At",
      width: 150,
      render: (value) =>
        value ? (
          <div className="date-cell">
            <span>{moment(value).format("DD MMM YYYY")}</span>
            <span className="date-time">{moment(value).format("HH:mm")}</span>
          </div>
        ) : (
          "-"
        ),
    },
    {
      key: "remark",
      label: "Remark",
      width: 140,
      render: (_, row) => {
        const remark = row?.remark || "";
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}>
            <span
              style={{
                flex: 1,
                maxWidth: "140px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: "13px",
                color: remark ? "#374151" : "#9ca3af",
                fontWeight: remark ? 400 : 300,
              }}
              title={remark || "No remark - Click to add"}
            >
              {remark || "-"}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                // Use originalOrderId for API call
                const orderForApi = { ...row, _id: row.originalOrderId || row._id };
                handleRemarkClick(orderForApi);
              }}
              style={{
                padding: "4px 10px",
                fontSize: "12px",
                border: "1px solid #3b82f6",
                background: remark ? "#eff6ff" : "#fff",
                borderRadius: "4px",
                cursor: "pointer",
                color: "#3b82f6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#3b82f6";
                e.currentTarget.style.color = "#fff";
                e.currentTarget.style.borderColor = "#3b82f6";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = remark ? "#eff6ff" : "#fff";
                e.currentTarget.style.color = "#3b82f6";
                e.currentTarget.style.borderColor = "#3b82f6";
              }}
              title={remark ? "Edit remark" : "Add remark"}
            >
              <i className="fas fa-edit" style={{ fontSize: "11px" }}>{remark ? "Edit" : "Add"}</i>
            </button>
          </div>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      width: 80,
      render: (_, row) => (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              // Use originalOrderId for API call
              const orderForApi = { ...row, _id: row.originalOrderId || row._id };
              handleDeleteClick(orderForApi);
            }}
            style={{
              padding: "4px 6px",
              fontSize: "14px",
              border: "1px solid #dc2626",
              background: "#fff",
              borderRadius: "4px",
              cursor: "pointer",
              color: "#dc2626",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
              boxShadow: "0 1px 3px rgba(220, 38, 38, 0.2)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#dc2626";
              e.currentTarget.style.color = "#fff";
              e.currentTarget.style.borderColor = "#dc2626";
              e.currentTarget.style.transform = "scale(1.1)";
              e.currentTarget.style.boxShadow = "0 2px 6px rgba(220, 38, 38, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#fff";
              e.currentTarget.style.color = "#dc2626";
              e.currentTarget.style.borderColor = "#dc2626";
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow = "0 1px 3px rgba(220, 38, 38, 0.2)";
            }}
            title="Delete order (requires password)"
          >
            <i className="fas fa-trash" style={{ fontSize: "12px" }}>Delete</i>
          </button>
        </div>
      ),
    },
  ];

  const toolbarLeftActions = (
    <div className="actions-left" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <button
        onClick={handleExportCsv}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: "6px 12px",
          fontSize: "12px",
          height: "32px",
          border: "1px solid #d0d7de",
          background: "#fff",
          color: "#24292f",
          borderRadius: "4px",
          cursor: "pointer",
        }}
      >
        <i className="fas fa-download"></i>
        Export Failed Orders
      </button>
    </div>
  );

  // Bulk actions for the table
  const bulkActions = [
    {
      key: "bulkDelete",
      label: "Bulk Delete",
      icon: <i className="fas fa-trash" style={{ marginRight: "6px" }}></i>,
    },
  ];

  const handleBulkAction = (actionKey) => {
    if (actionKey === "bulkDelete") {
      handleBulkDeleteClick();
    }
  };

  return (
    <div className="failed-order-table-container">
      <ExcelTable
        data={showFullExcelData ? flattenedData : tableData}
        columns={showFullExcelData ? fullExcelColumns : columns}
        isLoading={isLoading}
        loadingText={loadingText}
        onExport={handleExportCsv}
        showToolbarExport={true}
        showToolbarFilter={true}
        showToolbarColumns={true}
        showToolbarFullscreen={true}
        toolbarLeftActions={toolbarLeftActions}
        onSelectionChange={handleSelectionChange}
        selectedData={selectedOrders}
        selectable={true}
        sortable={true}
        resizable={true}
        showToolbar={true}
        title={showFullExcelData ? "Own Account Failed Orders - Full Excel Data" : "Failed Orders"}
        bulkActions={bulkActions}
        onBulkAction={handleBulkAction}
      />

      {/* Remark Modal */}
      {remarkModal.open && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setRemarkModal({ open: false, order: null, remark: "" })}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              padding: "24px",
              width: "500px",
              maxWidth: "90%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 16px 0", fontSize: "18px", fontWeight: 600 }}>
              Add/Edit Remark
            </h3>
            <p
              style={{
                margin: "0 0 12px 0",
                fontSize: "14px",
                color: "#6b7280",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                flexWrap: "wrap",
              }}
            >
              <span>
                Order ID: <strong>{remarkModal.order?.orderId}</strong>
              </span>
              {remarkModal.order ? <OrderPlatformBadge order={remarkModal.order} size={16} /> : null}
            </p>
            <textarea
              value={remarkModal.remark}
              onChange={(e) => setRemarkModal({ ...remarkModal, remark: e.target.value })}
              placeholder="Enter remark..."
              style={{
                width: "100%",
                minHeight: "100px",
                padding: "8px",
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                fontSize: "14px",
                fontFamily: "inherit",
                resize: "vertical",
              }}
            />
            <div style={{ display: "flex", gap: "8px", marginTop: "16px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setRemarkModal({ open: false, order: null, remark: "" })}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleRemarkSave}
                style={{
                  padding: "8px 16px",
                  border: "none",
                  background: "#3b82f6",
                  color: "#fff",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal.open && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setDeleteModal({ open: false, order: null, password: "", showPassword: false })}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              padding: "24px",
              width: "400px",
              maxWidth: "90%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 16px 0", fontSize: "18px", fontWeight: 600, color: "#dc2626" }}>
              Delete Failed Order
            </h3>
            <p
              style={{
                margin: "0 0 12px 0",
                fontSize: "14px",
                color: "#6b7280",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                flexWrap: "wrap",
              }}
            >
              <span>
                Order ID: <strong>{deleteModal.order?.orderId}</strong>
              </span>
              {deleteModal.order ? <OrderPlatformBadge order={deleteModal.order} size={16} /> : null}
            </p>
            <p style={{ margin: "0 0 16px 0", fontSize: "14px", color: "#dc2626" }}>
              This action cannot be undone. Please enter your admin password to confirm.
            </p>
            <div style={{ position: "relative", marginBottom: "16px" }}>
              <input
                type={deleteModal.showPassword ? "text" : "password"}
                value={deleteModal.password}
                onChange={(e) => setDeleteModal({ ...deleteModal, password: e.target.value })}
                placeholder="Enter admin password..."
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                style={{
                  width: "100%",
                  padding: "8px 40px 8px 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleDeleteConfirm();
                  }
                }}
              />
              <button
                type="button"
                onClick={() => setDeleteModal({ ...deleteModal, showPassword: !deleteModal.showPassword })}
                style={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  right: 0,
                  display: "flex",
                  alignItems: "center",
                  paddingRight: "12px",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "#64748b",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "#475569";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "#64748b";
                }}
                title={deleteModal.showPassword ? "Hide password" : "Show password"}
              >
                {deleteModal.showPassword ? (
                  <FiEyeOff size={16} />
                ) : (
                  <FiEye size={16} />
                )}
              </button>
            </div>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setDeleteModal({ open: false, order: null, password: "", showPassword: false })}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={!deleteModal.password}
                style={{
                  padding: "8px 16px",
                  border: "none",
                  background: deleteModal.password ? "#dc2626" : "#9ca3af",
                  color: "#fff",
                  borderRadius: "4px",
                  cursor: deleteModal.password ? "pointer" : "not-allowed",
                  fontSize: "14px",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Modal */}
      {bulkDeleteModal.open && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setBulkDeleteModal({ open: false, password: "", showPassword: false })}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              padding: "24px",
              width: "450px",
              maxWidth: "90%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 16px 0", fontSize: "18px", fontWeight: 600, color: "#dc2626" }}>
              Bulk Delete Failed Orders
            </h3>
            <p style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#6b7280" }}>
              You are about to delete <strong>{selectedOrders.length}</strong> failed order(s).
            </p>
            <p style={{ margin: "0 0 16px 0", fontSize: "14px", color: "#dc2626" }}>
              This action cannot be undone. Please enter your admin password to confirm.
            </p>
            <div style={{ position: "relative", marginBottom: "16px" }}>
              <input
                type={bulkDeleteModal.showPassword ? "text" : "password"}
                value={bulkDeleteModal.password}
                onChange={(e) => setBulkDeleteModal({ ...bulkDeleteModal, password: e.target.value })}
                placeholder="Enter admin password..."
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                style={{
                  width: "100%",
                  padding: "8px 40px 8px 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleBulkDeleteConfirm();
                  }
                }}
              />
              <button
                type="button"
                onClick={() => setBulkDeleteModal({ ...bulkDeleteModal, showPassword: !bulkDeleteModal.showPassword })}
                style={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  right: 0,
                  display: "flex",
                  alignItems: "center",
                  paddingRight: "12px",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "#64748b",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "#475569";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "#64748b";
                }}
                title={bulkDeleteModal.showPassword ? "Hide password" : "Show password"}
              >
                {bulkDeleteModal.showPassword ? (
                  <FiEyeOff size={16} />
                ) : (
                  <FiEye size={16} />
                )}
              </button>
            </div>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setBulkDeleteModal({ open: false, password: "", showPassword: false })}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDeleteConfirm}
                disabled={!bulkDeleteModal.password}
                style={{
                  padding: "8px 16px",
                  border: "none",
                  background: bulkDeleteModal.password ? "#dc2626" : "#9ca3af",
                  color: "#fff",
                  borderRadius: "4px",
                  cursor: bulkDeleteModal.password ? "pointer" : "not-allowed",
                  fontSize: "14px",
                }}
              >
                Delete {selectedOrders.length} Order(s)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FailedOrderTable;
